import { useEffect, useState, type ComponentType } from "react";
import { useSearchParams } from "react-router-dom";
import { useCrm } from "../../lib/session";
import { useAsync, type AsyncState } from "../../lib/useAsync";
import type { Crm } from "../../lib/crm";
import type { HumanTask } from "../../lib/types";
import { countWord, fmtDateTime, humanise, plural, relativeDue } from "../../lib/format";
import { Async, Empty, Notice, PageHead } from "../../components/ui";
import { Button, FoldCard, IconTile } from "../../components/kit";
import { kindLook } from "../../lib/kindLook";
import "./approvals.css";
import { OutboxBatch } from "./OutboxBatch";
import { SendByHandTask } from "./SendByHand";
import { SearchPromptTask } from "../find/SearchHealth";
import { promptSummary } from "../../lib/searchHealth";
import { plainProblem } from "../../lib/plainHealth";
import { ENGINE_TOWN } from "../../lib/engineTown.generated";
import { ConfigTask, ContentTask, DocumentTask, HealthTask, INTENTS, MetaPostCheckTask, OtherTask, ReplyTask, ReviewReplyTask, SameFirmTask } from "./TaskCards";
import { AutoApprovedList, EngineNoticeTask, levelLabel, PromotionTask } from "../Autonomy";
import { AttendanceTask, ChasersDecideTask, DealDecisionTask, MeetingNotesTask, MeetingSoonTask } from "./MeetingCards";
import { SyndicationTask } from "../attract/Syndication";
import { TARGET_LABEL, type SyndicationTarget } from "../../lib/syndication";
import { Link } from "react-router-dom";
import { useTaskTimer } from "../../lib/ownerTimeTracker";
import { InTouchTask } from "../find/CrmLink";
import { inTouchQuestion } from "../../lib/crmLink";

/** Calls (4.13) are worked on Today's calls, which has the brief and the outcome buttons. */
function CallElsewhere() {
  return (
    <p>
      <Link to="/settings/modules/calls">Open Today's calls</Link>
    </p>
  );
}

/** A WhatsApp update (10.13) is approved or declined on its own card, which shows who it goes to and what it costs. */
function WhatsAppUpdateElsewhere() {
  return (
    <p>
      <Link to="/reconnect/actions/waiting">Approve or decline it in ReConnect</Link>
    </p>
  );
}

/** LinkedIn by hand (4.5) is worked on its own page, with each owner's profile and the note to send. */
function LinkedinElsewhere() {
  return (
    <p>
      <Link to="/campaigns/to-approve">Open LinkedIn by hand</Link>
    </p>
  );
}
import { ChooseEventTask, EventPitchTask } from "../network/EventCards";
import { SaveOfferTask } from "./SaveOfferCard";
import { WhatsAppHandoffTask, WhatsAppReplyTask } from "./WhatsAppCards";
import { ChooseRouteTask, chooseRouteSummary, PathStepTask, pathStepSummary, quoteSummary } from "./PathStepCard";
import { PutItRightTask, putItRightSummary } from "./PutItRightCard";
import { PostReviewReplyTask, postReviewReplySummary, ReviewFeedbackTask, reviewFeedbackSummary } from "./ReviewTaskCards";
import { ApproveQuoteTask, approveQuoteSummary, ConfirmAnswersTask, confirmAnswersSummary, PortalAnswersTask, portalAnswersSummary, PortalQuestionTask, portalQuestionSummary, QuoteOutcomeTask, quoteOutcomeSummary } from "./QuoteCards";
import { OFFER_KIND_LABEL, FLAVOUR_LABEL, type Flavour, type OfferKind } from "../../lib/connect";
import "../screens-a.css";
import { CampaignChangeTask, campaignChangeSummary } from "../../components/find/CampaignChange";
import { LearningDigestTask, learningDigestSummary } from "../../components/find/LearningDigest";

interface KindDef {
  title: string;
  /** A one-word tag for lists that mix kinds (Home). */
  tag: string;
  step: string;
  summary: (t: HumanTask) => string;
  /** The row's second line in plain words, when the tag would read as jargon there (it stays the tag on Home). */
  rowTag?: string;
  /** The row's due words, when "due today" would read oddly for this kind. */
  dueWords?: (due: string) => string;
  Card: ComponentType<{ crm: Crm; task: HumanTask; onDone: () => void }>;
  /** A notice whose card has no Done of its own: the inbox adds Mark done (MODULE_NAVIGATION.md, "Notices and problems"). */
  markDone?: true;
}

export const KINDS: Record<string, KindDef> = {
  approve_outbox_batch: {
    title: "Messages to approve",
    tag: "Outreach",
    rowTag: "Waiting for your approval",
    dueWords: (due) => (due === "due today" || due === "due tomorrow" || /^due in/.test(due) ? "" : due),
    step: "4.10",
    summary: (t) => {
      const n = t.payload.count ? `${t.payload.count} ${Number(t.payload.count) === 1 ? "message" : "messages"} ready to send` : "A batch of messages ready to send";
      // A campaign started from Ask (4.11) says which one.
      return t.payload.campaign_name ? `Campaign “${String(t.payload.campaign_name)}”: ${n}` : n;
    },
    Card: OutboxBatch,
  },
  // 4.22-4.23 firms held for manual outreach only: Ogma drafts, the owner sends from their own account
  // (screens/approvals/SendByHand.tsx)
  send_by_hand: {
    title: "Messages to send yourself",
    tag: "By hand",
    step: "4.23",
    summary: (t) => {
      const n = Number(t.payload.count ?? 0);
      return n ? `${n} ${n === 1 ? "message" : "messages"} to send from your own Instagram, Facebook or email` : "Messages to send from your own accounts";
    },
    Card: SendByHandTask,
    markDone: true,
  },
  // 2.39 and 10.20 a search running low or its replies dropping (screens/find/SearchHealth.tsx)
  search_running_low: {
    title: "Your strongest matches are running low",
    tag: "Search",
    step: "2.39",
    summary: promptSummary,
    Card: SearchPromptTask,
  },
  // 2.38 TypeSafe (Jev) has no credits: every search's scoring is paused until they are added (migration 20270228090000)
  find_scoring_blocked: {
    title: "Scoring has stopped",
    tag: "Search",
    step: "2.38",
    summary: (t) => String(t.payload.what_to_do ?? "Add TypeSafe credits so searches can be scored again"),
    Card: OtherTask,
    markDone: true,
  },
  search_replies_dropped: {
    title: "Replies are dropping",
    tag: "Search",
    step: "10.20",
    summary: promptSummary,
    Card: SearchPromptTask,
  },
  decide_reply: {
    title: "Replies to read",
    tag: "Reply",
    step: "6.11",
    summary: (t) => {
      const label = INTENTS.find((i) => i.value === t.payload.suggested_intent)?.label;
      return label ? `A reply to check: probably “${label.toLowerCase()}”` : "A reply the inbox agent isn't sure about";
    },
    Card: ReplyTask,
  },
  review_reply: {
    title: "Replies for you to answer",
    tag: "Answer",
    step: "6.10",
    summary: (t) => (t.payload.objection ? `A reply for you to answer (objection ${t.payload.objection})` : "A reply for you to answer"),
    Card: ReviewReplyTask,
  },
  // 6.13 WhatsApp replies (screens/approvals/WhatsAppCards.tsx)
  approve_whatsapp_reply: {
    title: "WhatsApp replies to approve",
    tag: "WhatsApp",
    step: "6.13",
    summary: (t) => `WhatsApp reply to ${String(t.payload.name ?? "someone")}${t.payload.route === "sampled" ? " (spot check)" : ""}`,
    Card: WhatsAppReplyTask,
  },
  whatsapp_handoff: {
    title: "WhatsApp conversations passed to you",
    tag: "WhatsApp",
    step: "6.13",
    summary: (t) => `${String(t.payload.name ?? "Someone")} on WhatsApp: ${String(t.payload.reason ?? "the assistant passed this to you")}`,
    Card: WhatsAppHandoffTask,
  },
  approve_content: {
    title: "Posts and articles to approve",
    tag: "Post",
    step: "1.21",
    summary: (t) =>
      t.payload.kind === "pillar" || t.payload.kind === "blog"
        ? `Article: ${t.payload.title ? String(t.payload.title) : "for the blog"}`
        : `${t.payload.network ? humanise(String(t.payload.network)) : "Social"} post${t.payload.scheduled_at ? ` for ${fmtDateTime(String(t.payload.scheduled_at))}` : ""}`,
    Card: ContentTask,
  },
  syndication_pack: {
    title: "Re-post packs",
    tag: "Re-post",
    step: "1.56",
    summary: (t) => `${TARGET_LABEL[t.payload.target as SyndicationTarget] ?? humanise(String(t.payload.target ?? ""))}: “${String(t.payload.title ?? "the latest issue")}” ready to paste`,
    Card: SyndicationTask,
  },
  choose_event: {
    title: "Events to choose",
    tag: "Event",
    step: "2.24",
    summary: (t) => `Choose: ${String(t.payload.name ?? "an event")}${typeof t.payload.score === "number" ? ` (fit ${Math.round(t.payload.score)})` : ""}`,
    Card: ChooseEventTask,
  },
  approve_event_pitch: {
    title: "Pitches to event organisers",
    tag: "Pitch",
    step: "2.25",
    summary: (t) => String(t.payload.title ?? `Pitch for ${String(t.payload.name ?? "an event")}`),
    Card: EventPitchTask,
  },
  approve_document: {
    title: "Offers and contracts",
    tag: "Offer",
    step: "9.6",
    summary: (t) => `${t.payload.title ?? humanise(String(t.payload.kind ?? "document"))}`,
    Card: DocumentTask,
  },
  hold_meeting: {
    title: "Meetings coming up",
    tag: "Meeting",
    step: "8.3",
    summary: (t) => `Your meeting${t.payload.with ? ` with ${String(t.payload.with)}` : ""}${t.payload.when ? `, ${String(t.payload.when)}` : ""}`,
    Card: MeetingSoonTask,
  },
  read_brief: {
    title: "Briefs to read",
    tag: "Meeting",
    step: "8.1",
    summary: () => "Read the brief before your meeting",
    Card: MeetingSoonTask,
  },
  confirm_attendance: {
    title: "Meetings: did they happen?",
    tag: "Meeting",
    step: "8.D",
    summary: (t) => `Did the meeting happen?${t.payload.with ? ` (${String(t.payload.with)})` : ""}`,
    Card: AttendanceTask,
  },
  add_meeting_notes: {
    title: "Meeting notes to add",
    tag: "Notes",
    step: "8.4",
    summary: (t) => `Paste the notes from the meeting${t.payload.with ? ` with ${String(t.payload.with)}` : ""}${t.payload.organisation ? ` (${String(t.payload.organisation)})` : ""}`,
    Card: MeetingNotesTask,
  },
  decide_deal_outcome: {
    title: "Deal decisions",
    tag: "Deal",
    step: "9.D",
    summary: (t) => (t.payload.held ? "A deal you put on hold: decide again" : "No answer after the chases: decide what next"),
    Card: DealDecisionTask,
  },
  // 9.D after the chasers along a firm's path (engine/path_nudges.py): closing is a person's decision.
  decide_after_chasers: {
    title: "No answer after the chasers",
    tag: "Deal",
    step: "9.D",
    summary: (t) => `No answer after ${typeof t.payload.chases === "number" ? `${t.payload.chases} chasers` : "the chasers"}: call, wait or close`,
    Card: ChasersDecideTask,
  },
  approve_save_offer: {
    title: "Save offers to approve",
    tag: "Offer",
    step: "9.12",
    summary: (t) =>
      `Save offer for ${String(t.payload.organisation ?? "a deal")}: ${
        t.payload.offer_kind === "discount" && typeof t.payload.discount_pct === "number"
          ? `${t.payload.discount_pct}% off`
          : (OFFER_KIND_LABEL[t.payload.offer_kind as OfferKind] ?? "an offer").toLowerCase()
      }`,
    Card: SaveOfferTask,
  },
  prepare_quote: {
    title: "Quotes to prepare",
    tag: "Quote",
    step: "9.5",
    summary: quoteSummary,
    // Tied to a firm's path (the quote block): Done moves the path on (9.15); without a path, the plain card.
    Card: PathStepTask,
  },
  // 7.9, 7.12 booking (screens/pipeline/Booking.tsx): what Ogma could not finish, and a guest's cancellation
  booking_problem: {
    title: "Bookings that need you",
    tag: "Booking",
    step: "7.9",
    summary: (t) => `A booking Ogma could not finish${t.payload.reason ? `: ${String(t.payload.reason).slice(0, 120)}` : ""}`,
    Card: OtherTask,
  },
  // 7.1, 7.3 booking by email: no two free times to offer, and an answer to offered times Ogma could not read
  find_meeting_time: {
    title: "Meeting times to find",
    tag: "Booking",
    step: "7.1",
    summary: (t) => `No two free times to offer${t.payload.reason ? `: ${String(t.payload.reason)}` : ""}. Reply with times yourself or free up your calendar.`,
    Card: OtherTask,
  },
  decide_meeting_reply: {
    title: "Answers to offered times",
    tag: "Booking",
    step: "7.3",
    summary: (t) =>
      `Ogma could not tell which time they chose${Array.isArray(t.payload.slots) && t.payload.slots.length ? ` (offered ${t.payload.slots.map(String).join(", ")})` : ""}. Book it or reply yourself.`,
    Card: OtherTask,
  },
  meeting_cancelled: {
    title: "Cancelled meetings",
    tag: "Meeting",
    step: "7.12",
    summary: (t) =>
      `${t.payload.with ? String(t.payload.with) : "Someone"}${t.payload.organisation ? ` (${String(t.payload.organisation)})` : ""} cancelled${t.payload.starts_at ? ` the meeting on ${fmtDateTime(String(t.payload.starts_at))}` : ""}: offer new times?`,
    Card: OtherTask,
  },
  approve_quote: {
    title: "Quotes to approve",
    tag: "Quote",
    step: "9.17",
    summary: approveQuoteSummary,
    Card: ApproveQuoteTask,
  },
  confirm_quote_answers: {
    title: "Quote answers to check",
    tag: "Answers",
    step: "9.16",
    summary: confirmAnswersSummary,
    Card: ConfirmAnswersTask,
  },
  portal_answers: {
    title: "Answers from the quote portal",
    tag: "Answers",
    step: "9.27",
    summary: portalAnswersSummary,
    Card: PortalAnswersTask,
  },
  portal_question: {
    title: "Questions the assistant passed on",
    tag: "Question",
    step: "9.28",
    summary: portalQuestionSummary,
    Card: PortalQuestionTask,
  },
  quote_outcome: {
    title: "Quotes answered",
    tag: "Answer",
    step: "9.19",
    summary: quoteOutcomeSummary,
    Card: QuoteOutcomeTask,
  },
  path_step: {
    title: "Steps on a path",
    tag: "Step",
    step: "9.15",
    summary: pathStepSummary,
    Card: PathStepTask,
  },
  // 9.25 a route on the firm's path asks you where it goes next
  choose_route: {
    title: "Where firms go next",
    tag: "Route",
    step: "9.25",
    summary: chooseRouteSummary,
    Card: ChooseRouteTask,
  },
  // 10.19 an okay or unhappy answer to the happiness check (screens/approvals/PutItRightCard.tsx)
  put_it_right: {
    title: "Customers to put right",
    tag: "Put right",
    step: "10.19",
    summary: putItRightSummary,
    Card: PutItRightTask,
  },
  reconnect_return: {
    title: "Back from ReConnect",
    tag: "Back",
    step: "10.11",
    summary: (t) =>
      `${String(t.payload.organisation ?? "A firm")} is back${t.payload.flavour ? ` (${FLAVOUR_LABEL[t.payload.flavour as Flavour] ?? ""})` : ""}: ${
        t.payload.trigger === "score" ? "their interest score rose" : t.payload.trigger === "book" ? "they used a booking link" : t.payload.trigger === "offer_accepted" ? "they took the offer" : "pick the conversation up"
      }`,
    Card: OtherTask,
  },
  // 0.31 WhatsApp templates: approved (or edited) on Settings › Campaign defaults › WhatsApp.
  approve_whatsapp_templates: {
    title: "WhatsApp templates to approve",
    tag: "WhatsApp",
    step: "0.31",
    summary: () => "WhatsApp templates are ready for you to approve (Settings › Campaign defaults › WhatsApp)",
    Card: OtherTask,
  },
  approve_whatsapp_update: {
    title: "WhatsApp update to approve",
    tag: "WhatsApp",
    step: "4.10",
    summary: (t) =>
      `“${String(t.payload.title ?? "An update")}” to ${String(t.payload.recipients ?? "some")} people who opted in${t.payload.estimate_usd !== undefined ? `, about $${Number(t.payload.estimate_usd).toFixed(2)}` : ""} (ReConversations › WhatsApp updates)`,
    Card: WhatsAppUpdateElsewhere,
  },
  whatsapp_template_problem: {
    title: "WhatsApp template refused",
    tag: "WhatsApp",
    step: "0.31",
    summary: (t) => `Meta ${String(t.payload.status ?? "refused").toLowerCase()} the template ${String(t.payload.name ?? "")}${t.payload.reason ? ` (${String(t.payload.reason).toLowerCase().replace(/_/g, " ")})` : ""}`,
    Card: OtherTask,
  },
  // 1.46 An approved review reply to paste under the review (crm__review_reply_decided); Network › Actions › Reviews.
  post_review_reply: {
    title: "Review replies to post",
    tag: "Review",
    step: "1.46",
    summary: postReviewReplySummary,
    Card: PostReviewReplyTask,
  },
  // 1.45 A customer answered the review request unhappily (crm_flag_review_feedback): get in touch.
  review_feedback: {
    title: "Customers to put right",
    tag: "Review",
    step: "1.45",
    summary: reviewFeedbackSummary,
    Card: ReviewFeedbackTask,
  },
  // E8 A sample check found a message or post that broke the voice rules (crm_record_quality_check); that kind is back
  // to approve-all. Business-wide: Settings › Approval settings, where autonomy is.
  review_quality_failure: {
    title: "Engine alerts",
    tag: "Quality",
    step: "E8",
    summary: (t) => plainProblem(t, ENGINE_TOWN).title,
    Card: HealthTask,
  },
  // 1.6 Meta direct: a post Ogma couldn't confirm on Instagram or Facebook (crm_hold_meta_post).
  meta_post_check: {
    title: "Posts to check",
    tag: "Post",
    step: "1.6",
    summary: (t) =>
      t.payload.outcome_unknown === false
        ? `Meta refused ${t.payload.title ? `“${String(t.payload.title)}”` : "a post"} ${String(t.payload.attempts ?? 3)} times`
        : `Did ${t.payload.title ? `“${String(t.payload.title)}”` : "a post"} go out on ${humanise(String(t.payload.network ?? "Meta"))}?`,
    Card: MetaPostCheckTask,
  },
  approve_config: {
    title: "Playbook",
    tag: "Playbook",
    step: "0.5",
    summary: (t) => `New playbook (version ${t.payload.version})${t.payload.note ? `: ${t.payload.note}` : ""}`,
    Card: ConfigTask,
  },
  same_firm: {
    title: "Same firm?",
    tag: "Check",
    step: "2.32",
    summary: (t) => {
      const f = (t.payload.found ?? {}) as Record<string, unknown>;
      const e = (t.payload.existing ?? {}) as Record<string, unknown>;
      return `Same firm? ${String(f.name ?? "A firm found")} and ${String(e.name ?? "one in your CRM")}`;
    },
    Card: SameFirmTask,
  },
  // K-03 (items 32, 33): a colleague at a firm already in touch; Yes or No (crm_answer_in_touch).
  in_touch_check: { title: "Already in touch?", tag: "Check", step: "4.10", summary: inTouchQuestion, Card: InTouchTask },
  call_back: {
    title: "Calls to make",
    tag: "Call",
    step: "4.13",
    summary: (t) => (t.payload.note ? `Call back: ${String(t.payload.note)}` : "Someone asked for a call: it's on Today's calls"),
    Card: CallElsewhere,
    markDone: true,
  },
  linkedin_connect: {
    title: "Calls to make",
    tag: "LinkedIn",
    step: "4.5",
    summary: (t) => String(t.payload.title ?? "Connect with today's owners on LinkedIn"),
    Card: LinkedinElsewhere,
    markDone: true,
  },
  add_right_contact: {
    title: "Follow-ups",
    tag: "Contact",
    step: "6.9",
    summary: (t) => (t.payload.name ? `Add ${String(t.payload.name)}${t.payload.role ? ` (${String(t.payload.role)})` : ""}: the right person to speak to` : "Add the right person to speak to"),
    Card: OtherTask,
  },
  follow_up: {
    title: "Follow-ups",
    tag: "Follow-up",
    step: "4.12",
    summary: (t) => `${t.payload.title ? String(t.payload.title) : "Follow up"}${t.payload.name ? `: ${String(t.payload.name)}` : ""}${t.payload.firm && t.payload.firm !== t.payload.name ? ` (${String(t.payload.firm)})` : ""}`,
    Card: OtherTask,
  },
  review_tender: {
    title: "Tenders to decide on",
    tag: "Tender",
    step: "2.9",
    summary: (t) =>
      `Bid or not: ${String(t.payload.title ?? "a tender")}${t.payload.buyer ? ` (${String(t.payload.buyer)})` : ""}${
        t.payload.deadline ? `, closes ${fmtDateTime(String(t.payload.deadline))}` : ""
      }${t.payload.known ? ". You know this buyer" : ""}`,
    Card: OtherTask,
  },
  tender_reconnect: {
    title: "Get in touch before a tender",
    tag: "Tender",
    step: "10.0",
    summary: (t) => `Before the tender: get back in touch with ${String(t.payload.buyer ?? "this buyer")}${t.payload.title ? ` (${String(t.payload.title)})` : ""}`,
    Card: OtherTask,
  },
  autonomy_promotion: {
    title: "Earned autonomy",
    tag: "Autonomy",
    step: "E13",
    summary: (t) => (t.payload.title ? String(t.payload.title) : `Move ${String(t.payload.label ?? "a kind of message").toLowerCase()} to “${levelLabel(String(t.payload.to ?? ""))}”?`),
    Card: PromotionTask,
  },
  // Connect package 4: one campaign's step, proposed one level up by the weekly review (decided here or on Rules › Approvals)
  autonomy_scope_promotion: {
    title: "Earned autonomy",
    tag: "Autonomy",
    step: "E13",
    summary: (t) => (t.payload.title ? String(t.payload.title) : `Move ${String(t.payload.label ?? "a kind of message").toLowerCase()} for ${String(t.payload.campaign ?? "a campaign")} to “${levelLabel(String(t.payload.to ?? ""))}”?`),
    Card: PromotionTask,
  },
  autonomy_demoted: {
    title: "Engine alerts",
    tag: "Alert",
    step: "E13",
    summary: (t) => `Approvals back on for ${((t.payload.labels ?? []) as string[]).join(", ").toLowerCase() || "some messages"}`,
    Card: EngineNoticeTask,
  },
  budget_reached: {
    title: "Engine alerts",
    tag: "Budget",
    step: "E14",
    summary: (t) => `Budget reached: raise or wait (${t.payload.kind === "model" ? "AI" : humanise(String(t.payload.kind ?? ""))}, ${t.payload.period === "day" ? "today" : "this month"})`,
    Card: EngineNoticeTask,
  },
  // 0.24 / E3 a part of the engine failed three times in a row (crm_finish_engine_work); its schedule is in Settings
  engine_part_failing: {
    title: "Engine alerts",
    tag: "Alert",
    step: "E3",
    summary: (t) => plainProblem(t, ENGINE_TOWN).title,
    Card: HealthTask,
  },
  health_alert: {
    title: "Engine alerts",
    tag: "Alert",
    step: "E7",
    summary: (t) => plainProblem(t, ENGINE_TOWN).title,
    Card: HealthTask,
  },
  // Upgrades Oct 2026 (Wave 0; docs/specs/UPGRADES_2026_10.md): each job that raises one of these replaces its summary
  // and card through its own page, never here. The payload's summary line is shown until then.
  review_duplicates: {
    title: "Possible duplicates",
    tag: "CRM",
    step: "3.15",
    summary: (t) => String(t.payload.summary ?? "Possible duplicates to review"),
    Card: OtherTask,
  },
  owner_todo: {
    title: "Your tasks",
    tag: "Task",
    step: "3.18",
    summary: (t) => String(t.payload.summary ?? "A task you set yourself"),
    Card: OtherTask,
  },
  refresh_article: {
    title: "Articles to refresh",
    tag: "Content",
    step: "1.36",
    summary: (t) => String(t.payload.summary ?? "An article that has slipped in search"),
    Card: OtherTask,
  },
  engage_linkedin: {
    title: "LinkedIn posts to comment on",
    tag: "Social",
    step: "1.69",
    summary: (t) => String(t.payload.summary ?? "Today's LinkedIn posts to comment on"),
    Card: OtherTask,
  },
  ai_answer_fix: {
    title: "AI answers to fix",
    tag: "Content",
    step: "1.37",
    summary: (t) => String(t.payload.summary ?? "A buyer question where AI answers miss you"),
    Card: OtherTask,
  },
  notetaker_problem: {
    title: "Meeting notetaker problems",
    tag: "Meetings",
    step: "8.8",
    summary: (t) => String(t.payload.summary ?? "The notetaker could not take notes for a meeting"),
    Card: OtherTask,
  },
  meeting_followups: {
    title: "Meeting follow-ups",
    tag: "Meetings",
    step: "8.7",
    summary: (t) => String(t.payload.summary ?? "Follow-ups from a meeting to check"),
    Card: OtherTask,
  },
  payment_problem: {
    title: "Payment problems",
    tag: "Payments",
    step: "9.20",
    summary: (t) => String(t.payload.summary ?? "A payment that needs you"),
    Card: OtherTask,
  },
  missed_call: {
    title: "Missed calls",
    tag: "Call",
    step: "5.13",
    summary: (t) => String(t.payload.summary ?? "A missed call to answer"),
    Card: OtherTask,
  },
  reconnect_phone_line: {
    title: "Phone line to reconnect",
    tag: "Call",
    step: "5.13",
    summary: (t) => String(t.payload.summary ?? "Your phone line needs reconnecting"),
    Card: OtherTask,
  },
  signal_play: {
    title: "Signal plays",
    tag: "Outreach",
    step: "2.41",
    summary: (t) => String(t.payload.summary ?? "A signal worth acting on now"),
    Card: OtherTask,
  },
  letter_address_doubtful: {
    title: "Letter addresses to check",
    tag: "Letters",
    step: "4.26",
    summary: (t) => String(t.payload.summary ?? "A letter address Ogma is unsure of"),
    Card: OtherTask,
  },
  // 2.30 a change to who a running campaign targets or its offer, waiting for the owner (L-05; components/find/CampaignChange.tsx)
  approve_campaign_change: {
    title: "Campaign changes to approve",
    tag: "Campaigns",
    step: "2.30",
    summary: campaignChangeSummary,
    Card: CampaignChangeTask,
  },
  // The learning base: this week's lessons from results, already in use, each with a veto (components/find/LearningDigest.tsx)
  learning_digest: {
    title: "What Ogma learned this week",
    tag: "Learning",
    step: "10.8",
    summary: learningDigestSummary,
    Card: LearningDigestTask,
  },
  contact_finder_problem: {
    title: "Contact finder problems",
    tag: "Data",
    step: "3.3",
    summary: (t) => String(t.payload.summary ?? "A contact finder needs you"),
    Card: OtherTask,
  },
};

const ORDER = ["approve_outbox_batch", "send_by_hand", "search_running_low", "find_scoring_blocked", "search_replies_dropped", "decide_reply", "review_reply", "approve_whatsapp_reply", "whatsapp_handoff", "hold_meeting", "read_brief", "confirm_attendance", "add_meeting_notes", "approve_document", "approve_save_offer", "decide_deal_outcome", "decide_after_chasers", "quote_outcome", "approve_quote", "confirm_quote_answers", "portal_answers", "portal_question", "prepare_quote", "path_step", "choose_route", "reconnect_return", "review_tender", "tender_reconnect", "approve_content", "meta_post_check", "syndication_pack", "approve_event_pitch", "choose_event", "put_it_right", "review_feedback", "post_review_reply", "approve_whatsapp_update", "approve_whatsapp_templates", "whatsapp_template_problem", "call_back", "linkedin_connect", "add_right_contact", "find_meeting_time", "decide_meeting_reply", "booking_problem", "meeting_cancelled", "approve_config", "autonomy_promotion", "autonomy_scope_promotion", "same_firm", "in_touch_check", "follow_up", "budget_reached", "autonomy_demoted", "review_quality_failure", "engine_part_failing", "health_alert", "review_duplicates", "owner_todo", "refresh_article", "engage_linkedin", "ai_answer_fix", "notetaker_problem", "meeting_followups", "payment_problem", "missed_call", "reconnect_phone_line", "signal_play", "letter_address_doubtful", "contact_finder_problem", "approve_campaign_change", "learning_digest"];

function otherDef(kind: string): KindDef {
  return { title: "Other things to check", tag: "Check", step: "", summary: () => humanise(kind), Card: OtherTask };
}

export function groupTasks(tasks: HumanTask[]): { kind: string; def: KindDef; tasks: HumanTask[] }[] {
  const groups = new Map<string, HumanTask[]>();
  for (const t of tasks) {
    const key = KINDS[t.kind] ? t.kind : "other";
    groups.set(key, [...(groups.get(key) ?? []), t]);
  }
  return [...ORDER, "other"].filter((k) => groups.has(k)).map((k) => ({ kind: k, def: KINDS[k] ?? otherDef(k), tasks: groups.get(k)! }));
}

/** The inbox's Mark done on a notice (docs/specs/MODULE_NAVIGATION.md "What Mark done does"): closes the task as handled
 * there. If the problem is still there, the engine raises it again. */
function MarkDone({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <Button
          variant="secondary"
          small
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await crm.completeTask(task.id, "done", { handled: "inbox" });
              onDone();
            } catch (e) {
              setError((e as Error).message);
              setBusy(false);
            }
          }}
        >
          {busy ? "Saving…" : "Mark done"}
        </Button>
      </div>
    </>
  );
}

function TaskItem({ crm, task, def, open, level, goTo, markDone, onToggle, onDone }: { crm: Crm; task: HumanTask; def: KindDef; open: boolean; level: 3 | 4; goTo?: string | null; markDone: boolean; onToggle: () => void; onDone: () => void }) {
  const summary = KINDS[task.kind] ? def.summary(task) : humanise(task.kind);
  // E62 owner minutes: the card's active time from opening to deciding (lib/ownerTimeTracker.tsx)
  const decided = useTaskTimer(task.id, open);
  const look = kindLook(def.tag);
  const done = () => {
    decided();
    onDone();
  };
  const due = def.dueWords ? def.dueWords(relativeDue(task.due_at)) : relativeDue(task.due_at);
  // The shared Card as a row, controlled by the queue (one task open at a time, and only the open one's body is
  // mounted, so its timer runs only while it is open). No id: nothing outside the queue opens or remembers a task, and a
  // ?task= link opens a task through the queue.
  return (
    <li className={`task${open ? " is-open" : ""}`} id={`item-${task.id}`}>
      <FoldCard
        variant="row"
        level={level}
        persist={false}
        icon={look.icon}
        tone={look.tone}
        title={<span title={`Step ${task.step_id}`}>{summary}</span>}
        summary={def.rowTag ?? def.tag}
        meta={<span className={/overdue/.test(due) ? "is-late" : undefined}>{due}</span>}
        actions={goTo ? <Button variant="link" small to={goTo} aria-label={`Go there: ${summary}`}>Go there</Button> : undefined}
        open={open}
        onToggle={(o) => {
          if (o !== open) onToggle();
        }}
      >
        {open && (
          <div className="task-body" id={`task-${task.id}`}>
            <def.Card crm={crm} task={task} onDone={done} />
            {markDone && def.markDone && <MarkDone crm={crm} task={task} onDone={done} />}
          </div>
        )}
      </FoldCard>
    </li>
  );
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Which task a `?task=<id>` link asks for: none (no or bad id), still loading, found in the list, or gone (done). */
export function focusTask(id: string | null, tasks: Pick<HumanTask, "id">[] | undefined): { state: "none" | "loading" | "found" | "gone"; id: string | null } {
  if (!id || !UUID.test(id)) return { state: "none", id: null };
  if (!tasks) return { state: "loading", id };
  return { state: tasks.some((t) => t.id === id) ? "found" : "gone", id };
}

/** Tells the menu its waiting-on-you counts have changed. */
export const TASKS_CHANGED = "ogma:tasks-changed";

/**
 * The queue of decisions waiting for you, grouped by kind. Home shows every kind; a section's page shows
 * only its own kinds (lib/nav.ts TASK_HOMES), with the same cards.
 */
export function ApprovalQueue({
  kinds,
  title,
  emptyTitle = "Nothing is waiting for you",
  emptyText = "New outreach, posts, unclear replies and offers appear here when they need your decision.",
  hideWhenEmpty = false,
  autoOpen = true,
  filter,
  filterKey = "",
  onCount,
  nested = false,
  goThere,
  markDone = false,
  tasks: given,
}: {
  kinds?: string[];
  /** Told how many tasks are waiting each time the list loads (a card around the queue says it in its subtitle). */
  onCount?: (n: number) => void;
  /** Only the tasks that pass, of those kinds (a page showing one slice of a kind, like articles or one channel). */
  filter?: (t: HumanTask) => boolean;
  /** Names the filter, so a new one reloads the list. */
  filterKey?: string;
  title?: string;
  emptyTitle?: string;
  emptyText?: string;
  hideWhenEmpty?: boolean;
  autoOpen?: boolean;
  /** Inside a card that has its own heading (an inbox panel): the kinds' headings sit one level down. */
  nested?: boolean;
  /** Each task's Go there link (the inbox), or null for none. */
  goThere?: (t: HumanTask) => string | null;
  /** Offer Mark done on the notices whose card has no Done of its own (the inbox). */
  markDone?: boolean;
  /** The open tasks, when the page around the queue has already read them (the inbox): no read of its own. */
  tasks?: HumanTask[];
}) {
  const crm = useCrm();
  const kindsKey = (kinds ?? []).join(",");
  const pass = (t: HumanTask) => (!kinds || kinds.includes(t.kind)) && (!filter || filter(t));
  const fetched = useAsync(
    () => (given ? Promise.resolve([]) : crm.openHumanTasks().then((all) => all.filter(pass))),
    [crm, kindsKey, filterKey, Boolean(given)],
  );
  const passed = given?.filter(pass);
  // The page that passed the tasks reads them again on TASKS_CHANGED, so reload has nothing to do here.
  const tasks: AsyncState<HumanTask[]> = passed ? { data: passed, error: null, loading: false, reload: () => undefined } : fetched;
  const [open, setOpen] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // A link from an alert (6.12: /approvals?task=<id>, which lands here) opens that task and scrolls to it.
  const [params] = useSearchParams();
  const focus = focusTask(params.get("task"), tasks.data);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!tasks.data || focused) return;
    if (focus.state === "found") {
      setOpen(focus.id);
      setFocused(true);
      window.setTimeout(() => document.getElementById(`item-${focus.id}`)?.scrollIntoView?.({ block: "start" }), 0);
    } else if (focus.state === "gone" && !given) {
      // With tasks passed in, this queue holds only one slice; the page says when a linked task has gone.
      setDone("That one has already been dealt with.");
      setFocused(true);
    }
  }, [tasks.data, focus.state, focus.id, focused]);

  const waitingCount = tasks.data?.length;
  useEffect(() => {
    if (waitingCount !== undefined) onCount?.(waitingCount);
  }, [waitingCount, onCount]);

  useEffect(() => {
    // The first task in the order shown (on Drafts to approve, the batch of messages), not the first one loaded.
    if (autoOpen && open === null && tasks.data?.length && focus.state !== "found") setOpen(groupTasks(tasks.data)[0]!.tasks[0]!.id);
  }, [tasks.data, open, autoOpen, focus.state]);

  const finished = (id: string) => {
    setDone("Done. That's off your list.");
    setOpen(tasks.data?.find((t) => t.id !== id)?.id ?? null);
    tasks.reload();
    window.dispatchEvent(new Event(TASKS_CHANGED));
  };

  if (hideWhenEmpty && tasks.data && tasks.data.length === 0 && !done) return null;
  const Heading = title || nested ? "h3" : "h2";
  return (
    <section className={title ? "block queue" : "queue"} aria-label={title ?? "Waiting for you"}>
      {title && <h2>{title}</h2>}
      <Notice text={done} />
      <Async
        state={tasks}
        label="Loading what's waiting"
        empty={(d) =>
          d.length === 0 ? (
            <Empty title={emptyTitle}>
              <p>{emptyText}</p>
            </Empty>
          ) : null
        }
      >
        {(d) =>
          groupTasks(d).map((g) => (
            <section key={g.kind} className="group" aria-labelledby={`g-${g.kind}`}>
              <header className="group-head">
                <IconTile icon={kindLook(g.def.tag).icon} tone={kindLook(g.def.tag).tone} size="sm" />
                <Heading id={`g-${g.kind}`}>
                  {g.def.title}{" "}
                  <span className="pill-count">
                    <span className="sr-only">(</span>
                    {g.tasks.length}
                    <span className="sr-only">)</span>
                  </span>
                </Heading>
              </header>
              <ul className="tasks">
                {g.tasks.map((t) => (
                  <TaskItem key={t.id} crm={crm} task={t} def={g.def} open={open === t.id} level={title || nested ? 4 : 3} goTo={goThere?.(t)} markDone={markDone} onToggle={() => setOpen(open === t.id ? null : t.id)} onDone={() => finished(t.id)} />
                ))}
              </ul>
            </section>
          ))
        }
      </Async>
    </section>
  );
}

export function Approvals() {
  const crm = useCrm();
  const count = useAsync(() => crm.openHumanTasks().then((t) => t.length), [crm]);
  const n = count.data ?? 0;
  return (
    <div className="page page-a">
      <PageHead title="Approvals" lede={count.data !== undefined ? (n ? `${countWord(n).replace(/^./, (c) => c.toUpperCase())} ${plural(n, "decision")} waiting for you, most urgent first.` : undefined) : undefined} />
      <ApprovalQueue />
      <AutoApprovedList />
    </div>
  );
}
